import React, { useEffect, useState } from 'react';
import { Download, Edit2, Plus, Printer, Trash2, X, FileText, File, CheckCircle, Lock, Unlock } from 'lucide-react';
import { API_BASE, authFetch } from '../../constants';
import EditSampleModal from './EditSampleModal';
import AddSampleModal from './AddSampleModal';

function BatchDetailsModal({ batchId, onClose, onDelete, onGenerateCSV, onDownloadCSV, isAdmin }) {
  const [batchData, setBatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrintLabels, setShowPrintLabels] = useState(false);
  const [editingSample, setEditingSample] = useState(null);
  const [showAddSample, setShowAddSample] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [labResults, setLabResults] = useState(null);

  useEffect(() => {
    fetchBatchDetails();
    fetchLabResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const fetchBatchDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/batches/${batchId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      setBatchData(data);
    } catch (err) {
      console.error('Failed to fetch batch details:', err);
      alert('Failed to load batch details');
    } finally {
      setLoading(false);
    }
  };

  const fetchLabResults = async () => {
    try {
      const res = await authFetch(`${API_BASE}/batches/${batchId}/has-lab-results`);
      if (res.ok) {
        const data = await res.json();
        setLabResults(data);
      }
    } catch (err) {
      console.error('Failed to fetch lab results:', err);
    }
  };

  const downloadLabCSV = async () => {
    try {
      const res = await authFetch(`${API_BASE}/lab-results/batch/${batchId}/download-csv`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${batchId}_lab_results.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download lab CSV');
    }
  };

  const downloadLabPDF = async () => {
    try {
      const res = await authFetch(`${API_BASE}/lab-results/batch/${batchId}/download-pdf`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${batchId}_lab_results.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download lab PDF');
    }
  };

  const downloadKASExport = async () => {
    try {
      const res = await authFetch(`${API_BASE}/batches/${batchId}/download-kas-export`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Get the display batch ID for the filename
      const displayId = batchData?.batch?.display_batch_id || batchId;
      a.download = `${displayId}_enriched_results.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download KAS Export');
    }
  };

  const downloadSummarySheet = async () => {
    try {
      const res = await authFetch(`${API_BASE}/batches/${batchId}/download-summary-sheet`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Get the display batch ID for the filename
      const displayId = batchData?.batch?.display_batch_id || batchId;
      a.download = `${displayId}_summary_sheet.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download summary sheet');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/batches/${batchId}/status`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        setShowStatusMenu(false);
        fetchBatchDetails();
      } else {
        alert('Failed to update status');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleUnlockBatch = async () => {
    if (!window.confirm(`Unlock batch ${batchId}? Customer will be able to view their results.`)) {
      return;
    }
    
    try {
      const res = await authFetch(`${API_BASE}/batches/${batchId}/unlock`, { method: 'POST' });
      if (res.ok) {
        alert('Batch unlocked successfully!');
        fetchBatchDetails(); // Refresh to show updated status
      } else {
        alert('Failed to unlock batch');
      }
    } catch (err) {
      alert('Failed to unlock batch');
    }
  };

  const handleEditSample = (sample) => {
    setEditingSample(sample);
  };

  const handleDeleteSample = async (sampleId, sampleSequence) => {
    if (!window.confirm(`Delete sample ${sampleSequence}? This will recalculate bag IDs for remaining samples.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/samples/${sampleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.ok) {
        alert('Sample deleted successfully. Bag IDs recalculated.');
        fetchBatchDetails();
      } else {
        alert('Failed to delete sample');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleSaveSample = () => {
    setEditingSample(null);
    fetchBatchDetails();
  };

  const handleAddSample = () => {
    setShowAddSample(true);
  };

  const handleSaveNewSample = () => {
    setShowAddSample(false);
    fetchBatchDetails();
  };

  const getSampleSummary = (sample) => {
    const parts = [];
    parts.push(`Order# ${batchId}`);
    if (sample.company_name) parts.push(sample.company_name.toUpperCase());
    if (sample.farm_name) parts.push(sample.farm_name.toUpperCase());
    if (sample.field_name) parts.push(sample.field_name.toUpperCase());
    if (sample.sample_name) parts.push(sample.sample_name.toUpperCase());
    if (sample.zone) parts.push(`Zone: ${sample.zone}`);
    parts.push(`Bag: ${sample.bag_id}`);
    return parts.join(' • ');
  };

  const getTestsSummary = (sample) => {
    const optional = [];
    if (sample.test_cl) optional.push('Cl');
    if (sample.test_co) optional.push('Co');
    if (sample.test_mo) optional.push('Mo');
    if (sample.test_salts) optional.push('Salts');
    if (sample.test_bulk_den) optional.push('Bulk Density');
    if (sample.test_olsen) optional.push('Olsen');
    if (sample.test_nh3) optional.push('NH3');
    if (sample.test_no3) optional.push('NO3');
    if (sample.test_ssc) optional.push('SSC');
    const base = 'Standard Analysis';
    return optional.length > 0 ? `${base}, ${optional.join(', ')}` : base;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p className="text-gray-600">Loading batch details...</p>
        </div>
      </div>
    );
  }

  if (!batchData) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] flex flex-col">
          <div className="border-b px-6 py-4 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">Batch {batchData.batch.display_batch_id || batchData.batch.full_batch_id || batchId}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {batchData.batch.company_name} • {batchData.samples.length} samples •{' '}
                {new Date(batchData.batch.submission_date).toLocaleDateString()}
              </p>
              <div className="mt-2 flex items-center gap-3">
                {/* Lock Status Badge */}
                {batchData.batch.locked && (
                  <div className="flex items-center gap-1.5 text-sm px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 font-medium">
                    <Lock size={14} />
                    {batchData.batch.locked_reason || 'Locked'}
                  </div>
                )}
                
                <div className="relative">
                  <button
                    onClick={() => setShowStatusMenu(!showStatusMenu)}
                    className="text-sm px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-medium hover:bg-blue-200"
                  >
                    Status: {batchData.batch.status || 'Pending'} ▼
                  </button>
                  {showStatusMenu && (
                    <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-10 py-1">
                      <button
                        onClick={() => handleStatusChange('In Progress')}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        In Progress
                      </button>
                      <button
                        onClick={() => handleStatusChange('Sent to Lab')}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        Sent to Lab
                      </button>
                      <button
                        onClick={() => handleStatusChange('Completed')}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        Completed
                      </button>
                      <button
                        onClick={() => handleStatusChange('Pending')}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        Pending
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Unlock Button for Admins */}
                {isAdmin && batchData.batch.locked && (
                  <button
                    onClick={handleUnlockBatch}
                    className="flex items-center gap-1 text-sm px-3 py-1 rounded-full bg-orange-100 text-orange-700 font-medium hover:bg-orange-200"
                  >
                    <Unlock size={16} />
                    Unlock Batch
                  </button>
                )}
                
                <button
                  onClick={handleAddSample}
                  className="flex items-center gap-1 text-sm px-3 py-1 rounded-full bg-green-100 text-green-700 font-medium hover:bg-green-200"
                >
                  <Plus size={16} />
                  Add Sample
                </button>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Lab Results Section - Only show download buttons if unlocked OR if admin */}
            {labResults?.has_results && (isAdmin || !batchData.batch.locked) && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={20} className="text-green-600" />
                    <h3 className="font-semibold text-green-900">Lab Results Available</h3>
                  </div>
                  {labResults.control_id && (
                    <span className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                      Control ID: {labResults.control_id}
                    </span>
                  )}
                </div>
                <p className="text-sm text-green-800 mb-3">
                  {labResults.sample_count} sample{labResults.sample_count !== 1 ? 's' : ''} tested
                </p>
                <div className="flex gap-2">
                  {labResults.has_csv && (
                    <button
                      onClick={downloadLabCSV}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                    >
                      <FileText size={16} />
                      Download Lab CSV
                    </button>
                  )}
                  {labResults.has_pdf && (
                    <button
                      onClick={downloadLabPDF}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
                    >
                      <File size={16} />
                      Download Lab PDF
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={downloadKASExport}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                    >
                      <Download size={16} />
                      KAS Export
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {/* Locked Results Message for Non-Admin Users */}
            {!isAdmin && batchData.batch.locked && labResults?.has_results && (
              <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Lock size={20} className="text-yellow-600" />
                  <h3 className="font-semibold text-yellow-900">Lab Results Pending Release</h3>
                </div>
                <p className="text-sm text-yellow-800">
                  Your lab results have been received and are currently being reviewed. 
                  They will be available for download once released by our team.
                </p>
              </div>
            )}

            {/* Samples List */}
            <div className="space-y-3">
              {batchData.samples.map((sample) => {
                const summary = getSampleSummary(sample);
                const tests = getTestsSummary(sample);
                const isQuarantine = sample.quarantine;

                return (
                  <div key={sample.id} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-gray-900">Sample {sample.sample_sequence}</div>
                      <div className="flex items-center gap-2">
                        {isQuarantine && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-semibold">
                            QUARANTINE
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditSample(sample);
                          }}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Edit sample"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSample(sample.id, sample.sample_sequence);
                          }}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Delete sample"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="text-sm text-gray-700 mb-1">{summary}</div>
                    <div className="text-xs text-gray-600">{tests}</div>

                    {sample.crop && (
                      <div className="text-xs text-gray-500 mt-2">
                        Crop: {sample.crop}
                        {sample.yield_goal && ` • Yield Goal: ${sample.yield_goal} units/ac`}
                        {sample.program_level && ` • ${sample.program_level} Program`}
                        {sample.organic && ' • Organic'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t px-6 py-4 bg-gray-50">
            <div className="flex justify-between items-center">
              <button
                onClick={onDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <Trash2 size={18} />
                Delete Batch
              </button>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPrintLabels(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Printer size={18} />
                  Print Labels
                </button>

                <button
                  onClick={downloadSummarySheet}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Download size={18} />
                  Summary Sheet
                </button>

                {(() => {
                  const isKasInternal = batchData.batch.company_name === 'KAS Internal';
                  
                  // For KAS Internal batches (admin's own company), use simple workflow
                  if (isKasInternal && isAdmin) {
                    if (!batchData.batch.csv_generated) {
                      return (
                        <button
                          onClick={onGenerateCSV}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Generate CSV
                        </button>
                      );
                    } else {
                      // Allow re-downloading CSV even after initial download
                      return (
                        <button
                          onClick={onDownloadCSV}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <Download size={18} />
                          {batchData.batch.downloaded ? 'Download CSV Again' : 'Download CSV'}
                        </button>
                      );
                    }
                  }
                  
                  // For other companies, use controlled workflow
                  // User: Show Ready to Send button when CSV not generated (including Pending status)
                  if (!isAdmin && !batchData.batch.csv_generated) {
                    return (
                      <button
                        onClick={onGenerateCSV}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Ready to Send
                      </button>
                    );
                  }
                  
                  // User: Show confirmation when marked as Ready to Ship
                  if (!isAdmin && batchData.batch.csv_generated && batchData.batch.status === 'Ready to Ship') {
                    return (
                      <span className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium">
                        Ready to Ship
                      </span>
                    );
                  }
                  
                  // User: Show CSV Generated status after admin downloads
                  if (!isAdmin && batchData.batch.status === 'CSV Generated') {
                    return (
                      <span className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium">
                        CSV Generated
                      </span>
                    );
                  }
                  
                  // Admin: Show Download button only when status is Ready to Ship (for non-KAS Internal)
                  if (isAdmin && batchData.batch.csv_generated && batchData.batch.status === 'Ready to Ship') {
                    return (
                      <button
                        onClick={onDownloadCSV}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        <Download size={18} />
                        Download CSV
                      </button>
                    );
                  }
                  
                  // Admin: Allow re-downloading after initial download (for non-KAS Internal)
                  if (isAdmin && batchData.batch.downloaded && batchData.batch.status === 'CSV Generated') {
                    return (
                      <button
                        onClick={onDownloadCSV}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        <Download size={18} />
                        Download CSV Again
                      </button>
                    );
                  }
                  
                  return null;
                })()}

                <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPrintLabels && (
        <PrintLabelsModal
          batchId={batchId}
          samples={batchData.samples}
          getSampleSummary={getSampleSummary}
          getTestsSummary={getTestsSummary}
          onClose={() => setShowPrintLabels(false)}
        />
      )}

      {editingSample && (
        <EditSampleModal
          sample={editingSample}
          onClose={() => setEditingSample(null)}
          onSave={handleSaveSample}
        />
      )}

      {showAddSample && (
        <AddSampleModal
          batchId={batchId}
          onClose={() => setShowAddSample(false)}
          onSave={handleSaveNewSample}
        />
      )}
    </>
  );
}

function PrintLabelsModal({ batchId, samples, getSampleSummary, getTestsSummary, onClose }) {
  const copyToClipboard = () => {
    const text = samples
      .map((sample) => {
        const summary = getSampleSummary(sample);
        const tests = getTestsSummary(sample);
        const quarantine = sample.quarantine ? '\n**QUARANTINE**' : '';
        return `Sample ${sample.sample_sequence}\n${summary}\n${tests}${quarantine}\n`;
      })
      .join('\n---\n\n');

    navigator.clipboard.writeText(text).then(() => {
      alert('Label text copied to clipboard!');
      onClose();
    });
  };

  const printLabels = () => {
    const labelHTML = samples
      .map((sample) => {
        const summary = getSampleSummary(sample);
        const tests = getTestsSummary(sample);
        const quarantine = sample.quarantine
          ? '<div style="color: red; font-weight: bold; margin-top: 8px;">**QUARANTINE**</div>'
          : '';

        return `
        <div style="page-break-after: always; padding: 20px; border: 2px solid #000; margin-bottom: 20px; font-family: Arial;">
          <div style="font-weight: bold; font-size: 14px; margin-bottom: 10px;">Sample ${sample.sample_sequence}</div>
          <div style="font-size: 12px; margin-bottom: 8px;">${summary}</div>
          <div style="font-size: 10px; color: #666;">${tests}</div>
          ${quarantine}
        </div>
      `;
      })
      .join('');

    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Labels - Batch ${batchId}</title>
          <style>
            body { margin: 0; padding: 20px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          ${labelHTML}
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 100);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-bold">Print Labels - Batch {batchId}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Label Printing Options</strong>
                <br />
                Use "Print to Browser" for standard printers or "Copy to Clipboard" to paste into your label software.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-3">Label Preview ({samples.length} labels)</h4>
              <div className="space-y-3">
                {samples.map((sample) => {
                  const summary = getSampleSummary(sample);
                  const tests = getTestsSummary(sample);
                  const isQuarantine = sample.quarantine;

                  return (
                    <div key={sample.id} className="border rounded-lg p-4 bg-gray-50 font-mono text-xs">
                      <div className="font-bold mb-1">Sample {sample.sample_sequence}</div>
                      <div className="mb-1">{summary}</div>
                      <div className="text-gray-600">{tests}</div>
                      {isQuarantine && <div className="text-red-600 font-bold mt-1">**QUARANTINE**</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t px-6 py-4 bg-gray-50 flex justify-between">
          <button onClick={copyToClipboard} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
            Copy to Clipboard
          </button>

          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              Cancel
            </button>
            <button
              onClick={printLabels}
              className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Printer size={18} />
              Print to Browser
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BatchDetailsModal;

